import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Search, RefreshCw, Mail, ChevronLeft, ChevronRight, ExternalLink,
  CheckCircle2, Eye, MousePointerClick, AlertTriangle, Send, Clock, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatRel } from "./constants";
import DomainBanner from "./DomainBanner";
import OutreachInboxDrawer from "./OutreachInboxDrawer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILL = {
  pending: { tone: "bg-slate-100 text-slate-700 ring-slate-200", icon: Clock },
  sent: { tone: "bg-cyan-50 text-cyan-700 ring-cyan-200", icon: Send },
  delivered: { tone: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: CheckCircle2 },
  opened: { tone: "bg-emerald-100 text-emerald-800 ring-emerald-300", icon: Eye },
  clicked: { tone: "bg-amber-50 text-amber-700 ring-amber-200", icon: MousePointerClick },
  bounced: { tone: "bg-rose-50 text-rose-700 ring-rose-200", icon: AlertTriangle },
  complained: { tone: "bg-rose-100 text-rose-800 ring-rose-300", icon: AlertTriangle },
  failed: { tone: "bg-rose-50 text-rose-700 ring-rose-200", icon: XCircle },
  delayed: { tone: "bg-amber-50 text-amber-700 ring-amber-200", icon: Clock },
};

const STATUS_FILTER_ORDER = [
  "sent", "delivered", "opened", "clicked", "bounced", "failed",
];

export default function AdminCrmOutreachInboxPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openLogId, setOpenLogId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/crm/outreach-inbox`, {
        params: {
          page,
          per_page: 25,
          q: q || undefined,
          status: status || undefined,
        },
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, [page, q, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, status]);

  return (
    <div className="space-y-5" data-testid="crm-outreach-inbox-page">
      <DomainBanner />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM · Outreach Inbox</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every email sent from the AI Outreach modal, with live Resend delivery status.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="crm-inbox-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="crm-inbox-status-row">
        <button
          onClick={() => setStatus("")}
          className={`text-xs px-2.5 py-1 rounded-full ring-1 ${!status ? "bg-cyan-700 text-white ring-cyan-700" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"}`}
          data-testid="crm-inbox-filter-all"
        >
          All <span className="ml-1 opacity-70">{data?.total ?? 0}</span>
        </button>
        {STATUS_FILTER_ORDER.map((s) => {
          const count = data?.status_counts?.[s] ?? 0;
          if (count === 0 && status !== s) return null;
          const active = status === s;
          const meta = STATUS_PILL[s] || STATUS_PILL.sent;
          const Icon = meta.icon;
          return (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`text-xs px-2.5 py-1 rounded-full ring-1 inline-flex items-center gap-1 ${
                active ? "bg-slate-900 text-white ring-slate-900" : `${meta.tone} hover:opacity-80`
              }`}
              data-testid={`crm-inbox-filter-${s}`}
            >
              <Icon className="w-3 h-3" />
              {s} <span className="ml-1 opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject, recipient, or body…"
            className="w-full text-sm rounded-lg border border-slate-200 pl-9 pr-3 py-2 bg-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="crm-inbox-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Mail className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No outreach emails yet.</p>
            <p className="text-xs text-slate-400 mt-1">Generate a draft and send via the AI Outreach modal on a contact.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="crm-inbox-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Subject</th>
                  <th className="px-3 py-2.5 font-semibold">Recipient</th>
                  <th className="px-3 py-2.5 font-semibold">Contact</th>
                  <th className="px-3 py-2.5 font-semibold">Channel</th>
                  <th className="px-3 py-2.5 font-semibold">Sent</th>
                  <th className="px-3 py-2.5 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((row) => {
                  const meta = STATUS_PILL[row.status] || STATUS_PILL.sent;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setOpenLogId(row.id)}
                      className="hover:bg-slate-50 cursor-pointer"
                      data-testid={`crm-inbox-row-${row.id}`}
                    >
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-md ring-1 inline-flex items-center gap-1 ${meta.tone}`}>
                          <Icon className="w-3 h-3" />
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 max-w-md">
                        <div className="font-medium text-slate-900 truncate">{row.subject || "—"}</div>
                        {row.error_message && (
                          <div className="text-xs text-rose-600 mt-0.5 truncate" title={row.error_message}>
                            {row.error_message}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700 truncate max-w-[200px]">
                        <a
                          href={`mailto:${row.to_email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-cyan-700 inline-flex items-center gap-1"
                        >
                          {row.to_email}
                        </a>
                      </td>
                      <td className="px-3 py-2.5">
                        {row.contact ? (
                          <Link
                            to={`/admin/crm/contacts/${row.crm_contact_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-cyan-700 hover:underline inline-flex items-center gap-1"
                            data-testid={`crm-inbox-contact-link-${row.crm_contact_id}`}
                          >
                            {row.contact.name}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{row.channel || "email"}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatRel(row.created_at)}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                        {row.last_webhook_at ? (
                          <span title={row.last_webhook_event}>{formatRel(row.last_webhook_at)}</span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data?.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <div className="text-slate-500">Page {data.page} of {data.total_pages} · {data.total} total</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {openLogId && (
        <OutreachInboxDrawer
          logId={openLogId}
          onClose={() => setOpenLogId(null)}
          onResent={() => load()}
        />
      )}
    </div>
  );
}
